import type { RowData } from "@tanstack/react-table";
import type { AppColumnDef } from "./useDataTable";

/**
 * Extended column meta for CustomTableWrapper / TanStack Table column defs.
 * @see CustomTableWrapper column visibility popover and column reordering
 */
export type TableColumnMeta = {
	headerProps?: { isRowHeader?: boolean };
	/**
	 * Leading featured-icon gutter (Figma entity tables).
	 * Fixed width + symmetric 20px horizontal padding — not a full text column.
	 */
	isIconGutter?: boolean;
	/**
	 * Leading selection gutter (radio/checkbox column that is not Aria `selection`).
	 * Fixed 52px + 32px left / 4px right padding — not a standard BodyCell.
	 */
	isSelectionGutter?: boolean;
	/** When true, this column cannot be reordered (header drag + Manage Columns list). */
	disableColumnReorder?: boolean;
	/**
	 * When true, skip the shared 1-line cell clamp for this column.
	 * @deprecated Prefer column-specific cell renderers; clamp is applied in CustomTableWrapper.
	 */
	disableDescriptionClamp?: boolean;
	/** When true, skip the shared 1-line cell clamp for this column. */
	disableCellClamp?: boolean;
	/** Access column: hover lists granted sub-organizations (org has sub-orgs). */
	accessTooltip?: (row: unknown) => string | undefined;
	/** On the row actions column: Cmd/Ctrl+click on the row invokes this (same as View full details). */
	rowFullViewAction?: (row: unknown) => void;
};

export function getTableColumnMeta(meta: unknown): TableColumnMeta | undefined {
	return meta as TableColumnMeta | undefined;
}

/** Safely reads the string id used by shared full-view row actions. */
export function getTableRowId(row: unknown): string | undefined {
	if (typeof row !== "object" || row === null || !("id" in row)) {
		return undefined;
	}
	return typeof row.id === "string" ? row.id : undefined;
}

/**
 * Row-header columns (e.g. primary "name") stay visible when using Toggle all;
 * they are not the same as reorder-locked columns.
 */
export function isRowHeaderColumn<TData extends RowData>(
	columnDef: AppColumnDef<TData>,
): boolean {
	return getTableColumnMeta(columnDef.meta)?.headerProps?.isRowHeader === true;
}

/**
 * Returns true when the column should not participate in reorder drag.
 * Only explicit `meta.disableColumnReorder` (e.g. actions column) — row-header columns remain reorderable.
 */
export function isColumnReorderDisabled<TData extends RowData>(
	columnDef: AppColumnDef<TData>,
): boolean {
	return getTableColumnMeta(columnDef.meta)?.disableColumnReorder === true;
}

/** Checkbox disabled in Manage columns (row header: cannot hide primary column). */
export function isColumnVisibilityToggleDisabled<TData extends RowData>(
	columnDef: AppColumnDef<TData>,
): boolean {
	return isRowHeaderColumn(columnDef);
}

export function isColumnReorderLockedById<TData extends RowData>(
	columnId: string,
	columnDefs: AppColumnDef<TData>[],
): boolean {
	const def = columnDefs.find((c) => c.id === columnId);
	if (!def) return false;
	return isColumnReorderDisabled(def);
}

/** Standard row-actions column id used across admin configuration tables. */
export function isActionsColumnId(columnId: string): boolean {
	return columnId === "actions";
}

/** Featured-icon gutter column (Jobs / Tickets / Assets / Records). */
export function isIconGutterColumn<TData extends RowData>(
	columnDef: AppColumnDef<TData> | { meta?: unknown },
): boolean {
	return getTableColumnMeta(columnDef.meta)?.isIconGutter === true;
}

/** Selection-control gutter (e.g. parent-picker radio column). */
export function isSelectionGutterColumn<TData extends RowData>(
	columnDef: AppColumnDef<TData> | { meta?: unknown },
): boolean {
	return getTableColumnMeta(columnDef.meta)?.isSelectionGutter === true;
}
